import { useState } from "react";
import { FaFileAlt, FaTimes, FaCloudUploadAlt, FaCheckCircle, FaExclamationTriangle } from "react-icons/fa";
import { ClipLoader } from "react-spinners";
import processPrescriptionFile from "../Aireports/processPrescriptionImage";

const FileUpload = () => {
  const [files, setFiles] = useState([]);
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files);
    resetState(); // Reset previous files and result
    processFiles(selectedFiles);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const droppedFiles = Array.from(event.dataTransfer.files);
    resetState(); // Reset previous files and result
    processFiles(droppedFiles);
  };

  const resetState = () => {
    setFiles([]); // Clear previous files
    setResult(""); // Clear previous result
  };

  const processFiles = (selectedFiles) => {
    const updatedFiles = selectedFiles.map((file) => ({
      name: file.name,
      size: file.size,
      progress: 0,
      status: file.size > 5 * 1024 * 1024 ? "error" : "uploading",
      file: file,
    }));

    setFiles(updatedFiles); // Overwrite previous files

    updatedFiles.forEach((file, index) => {
      if (file.status === "error") return;
      simulateUpload(index);
    });
  };

  const simulateUpload = (index) => {
    const interval = setInterval(() => {
      setFiles((prevFiles) => {
        const newFiles = [...prevFiles];
        if (newFiles[index].progress < 100) {
          newFiles[index].progress += 10;
        } else {
          newFiles[index].status = "completed";
          clearInterval(interval);
        }
        return newFiles;
      });
    }, 500);
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleUploadAndAnalyze = async () => {
    const completedFiles = files.filter((file) => file.status === "completed");
    if (completedFiles.length === 0) {
      alert("Please upload and complete at least one file.");
      return;
    }
    setLoading(true);
    setResult("");
    try {
      const responses = await Promise.all(completedFiles.map((file) => processPrescriptionFile(file.file)));
      setResult(responses.join("\n"));
    } catch (error) {
      console.error("Error processing files:", error);
      setResult("Error processing the files.");
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
    else return (bytes / 1048576).toFixed(1) + " MB";
  };

  return (
    <div className="upload-container">
      <div className="upload-header1">
        <h2 className="upload-title">Upload Prescription</h2>
        <p className="upload-description">Upload documents you want to analyze</p>
      </div>

      <div
        className={`drop-zone ${isHovering ? "drop-zone-hover" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setIsHovering(true);
        }}
        onDragLeave={() => setIsHovering(false)}
        onDrop={(e) => {
          handleDrop(e);
          setIsHovering(false);
        }}
      >
        <div className="drop-zone-content">
          <FaCloudUploadAlt className="upload-icon" />
          <input
            type="file"
            accept="application/pdf, image/*"
            multiple
            onChange={handleFileChange}
            id="fileInput"
            hidden
          />
          <label htmlFor="fileInput" className="browse-btn">
            Browse Files
          </label>
          <p className="file-type-hint">Accepts PDF and image files</p>
        </div>
      </div>

      {files.length > 0 && (
        <div className="files-container">
          <div className="files-header">
            <h3 className="files-title">Uploaded Files</h3>
            <span className="files-count">{files.length} file(s)</span>
          </div>
          <div className="uploaded-files">
            {files.map((file, index) => (
              <div className={`file-item ${file.status}`} key={index}>
                <div className="file-info">
                  <FaFileAlt className="file-icon" />
                  <div className="file-details">
                    <div className="file-name">{file.name}</div>
                    <div className="file-size">{formatFileSize(file.size)}</div>
                  </div>
                </div>

                <div className="file-status">
                  {file.status === "uploading" && (
                    <>
                      <div className="progress-container">
                        <div className="progress-bar">
                          <div className="progress" style={{ width: `${file.progress}%` }}></div>
                        </div>
                        <span className="progress-text">{file.progress}%</span>
                      </div>
                      <ClipLoader size={15} color={"#6366f1"} />
                    </>
                  )}

                  {file.status === "completed" && (
                    <div className="status-indicator completed">
                      <FaCheckCircle />
                      <span>Completed</span>
                    </div>
                  )}

                  {file.status === "error" && (
                    <div className="status-indicator error">
                      <FaExclamationTriangle />
                      <span>File size is too large</span>
                    </div>
                  )}

                  <button className="remove-btn" onClick={() => removeFile(index)}>
                    <FaTimes />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <button
        className={`upload-btn ${loading ? "loading" : ""} ${files.length === 0 ? "disabled" : ""}`}
        onClick={handleUploadAndAnalyze}
        disabled={loading || files.length === 0}
      >
        {loading ? (
          <>
            <ClipLoader size={18} color={"#ffffff"} />
            <span>Processing...</span>
          </>
        ) : (
          "Upload & Analyze"
        )}
      </button>

      {result && !loading && (
        <div className="result-container">
          <h3 className="result-title"><strong>Extracted Prescription Details</strong></h3>
          <div className="result-content">
            {result.split("\n").map((line, index) => {
              if (line.startsWith("### ")) {
                return <h3 key={index} className="result-heading"><strong>{line.replace("### ", "")}</strong></h3>;
              } else if (line.startsWith("#### ")) {
                return <h4 key={index} className="result-subheading"><strong>{line.replace("#### ", "")}</strong></h4>;
              } else if (line.startsWith("- **")) {
                return <p key={index} className="result-bold">{line}</p>;
              } else if (line.startsWith("- ")) {
                return <p key={index} className="result-list">{line}</p>;
              } else {
                return <p key={index} className="result-text">{line}</p>;
              }
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
